import React, { FormEvent } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import * as yup from 'yup'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { AppLayout } from '@/components/layouts/AppLayout'

interface NotebookAgentData {
  id: number
  role: 'admin' | 'editor' | 'reader'
  agent: {
    id: number
    username: string
    slug: string
    agent_type: 'human' | 'machine'
  }
  created_at: string
}

interface AvailableAgent {
  id: number
  username: string
  slug: string
}

interface Props {
  notebook: { id: number; name: string; slug: string }
  notebook_agents: NotebookAgentData[]
  available_agents: AvailableAgent[]
}

const memberSchema = yup.object({
  notebook_agent: yup.object({
    agent_id: yup.number().transform((value, original) => (original === '' ? undefined : value)).required('Agent is required'),
    role: yup.string().required('Role is required').oneOf(['admin', 'editor', 'reader']),
  })
})

function validateForm(schema: yup.ObjectSchema<any>, data: any, setError: (errors: Record<string, string>) => void): boolean {
  try {
    schema.validateSync(data, { abortEarly: false })
    return true
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      const validationErrors: Record<string, string> = {}
      err.inner.forEach((e) => {
        if (e.path) validationErrors[e.path] = e.message
      })
      setError(validationErrors)
    }
    return false
  }
}

function MemberForm({ notebookSlug, availableAgents }: { notebookSlug: string; availableAgents: AvailableAgent[] }) {
  const { data, setData, post, processing, errors, setError, clearErrors, reset } = useForm({
    notebook_agent: {
      agent_id: '' as number | '',
      role: 'reader',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()
    if (!validateForm(memberSchema, data, setError)) return

    post(`/notebooks/notebooks/${notebookSlug}/members`, {
      onSuccess: () => reset(),
    })
  }

  if (availableAgents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        All agents are already members of this notebook.
      </Typography>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2 }}>
      <FormControl size="small" sx={{ flex: 1 }} error={'notebook_agent.agent_id' in errors}>
        <InputLabel>Agent</InputLabel>
        <Select
          value={data.notebook_agent.agent_id}
          label="Agent"
          onChange={e => setData('notebook_agent', { ...data.notebook_agent, agent_id: e.target.value as number })}
        >
          {availableAgents.map((a) => (
            <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
          ))}
        </Select>
        {'notebook_agent.agent_id' in errors && (
          <FormHelperText>{(errors as Record<string, string>)['notebook_agent.agent_id']}</FormHelperText>
        )}
      </FormControl>
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Role</InputLabel>
        <Select
          value={data.notebook_agent.role}
          label="Role"
          onChange={e => setData('notebook_agent', { ...data.notebook_agent, role: e.target.value })}
        >
          <MenuItem value="reader">Reader</MenuItem>
          <MenuItem value="editor">Editor</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </Select>
      </FormControl>
      <Button type="submit" variant="contained" size="medium" disabled={processing}>
        Add
      </Button>
    </Box>
  )
}

export default function Settings({ notebook, notebook_agents, available_agents }: Props) {
  function handleRoleChange(na: NotebookAgentData, newRole: string) {
    router.put(`/notebooks/notebooks/${notebook.slug}/members/${na.id}`, {
      notebook_agent: { role: newRole },
    })
  }

  function handleRemoveMember(na: NotebookAgentData) {
    if (confirm(`Remove ${na.agent.username} from this notebook?`)) {
      router.delete(`/notebooks/notebooks/${notebook.slug}/members/${na.id}`)
    }
  }

  return (
    <AppLayout title={`${notebook.name} — Settings`}>
      <Box sx={{ maxWidth: 900 }}>
        <Box sx={{ mb: 2 }}>
          <Button
            component={Link}
            href={`/notebooks/notebooks/${notebook.slug}`}
            size="small"
          >
            Back to notebook
          </Button>
          <Button
            component={Link}
            href={`/notebooks/notebooks/${notebook.slug}/edit`}
            size="small"
            sx={{ ml: 1 }}
          >
            Edit notebook
          </Button>
        </Box>

        <Typography variant="h2" sx={{ mb: 2 }}>Members</Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notebook_agents.map((na) => (
                <TableRow key={na.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {na.agent.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={na.agent.agent_type}
                      size="small"
                      variant="outlined"
                      color={na.agent.agent_type === 'machine' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={na.role}
                        onChange={e => handleRoleChange(na, e.target.value)}
                        size="small"
                      >
                        <MenuItem value="reader">Reader</MenuItem>
                        <MenuItem value="editor">Editor</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveMember(na)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {notebook_agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No members.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <MemberForm notebookSlug={notebook.slug} availableAgents={available_agents} />
      </Box>
    </AppLayout>
  )
}
